import { useQueryClient } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import {
  base_colorContentBorder,
  base_colorSurfaceSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import SavedViewsIcon from 'ext-ui/svg/icon/SavedViewsIcon'
import moment, { Moment } from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectAllUsersFromState } from 'src/app/AccountTransactions/redux/accountTransactions.selectors'
import DeleteViewConfirmationModal from 'src/component-modal/DeleteViewConfirmationModal/DeleteViewConfirmationModal'
import { CreditCardDropdownOptionDetails } from 'src/components/CreditCardDropdownV2/CreditCardDropdownOption'
import { DropdownValue } from 'src/components/CreditCardDropdownV2/CreditCardDropdownV2'
import UtilityToolbar from 'src/components/DataGrid/UtilityToolbar/UtilityToolbar'
import UtilityToolbarDropdown from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import DateFilterDropdown from 'src/components/DateFilterDropdown/DateFilterDropdown'
import {
  DateRangeType,
  getDateRangeTypeFromKey,
  getDateRangeTypeKey,
} from 'src/components/DateFilterDropdown/dateRangeType'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SavedViewsDropdown from 'src/components/SavedViewsDropdown/SavedViewsDropdown'
import SvgCalendarNew from 'src/components/Svgs/SvgCalendarNew'
import SvgToolbarFullscreen from 'src/components/Svgs/SvgToolbarFullscreen'
import SvgToolbarFullscreenExit from 'src/components/Svgs/SvgToolbarFullscreenExit'
import {
  AccountFilter,
  CardUserFilter,
  DepartmentsFilter,
  ExpenseCategoryLabelsFilter,
  ExpenseCategoryStatusFilter,
  TransactionAmountRangeFilter,
  TransactionDateTypeFilter,
  TransactionMerchantCategoryFilter,
  TransactionOriginFilter,
  TransactionReceiptRecoveryStatusFilter,
  TransactionReceiptStatusFilter,
  TransactionReviewStatusFilter,
  TransactionStatusFilter,
  TransactionSyncStatusFilter,
} from 'src/components/TransactionsListFilter/filters'
import ReceiptSyncStatusFilter from 'src/components/TransactionsListFilter/filters/ReceiptSyncStatusFilter'
import {
  TransactionsFilters,
  TransactionsFiltersBody,
} from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { TransactionFilterOptions } from 'src/enums'
import {
  FormFieldsValidation,
  useDebounceValue,
  useFormFields,
  useStringSetState,
} from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useDropdownInfiniteScroll } from 'src/hooks/useDropdownInfiniteScroll'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  OrgExpenseReportCycleGroup,
  OrgExpenseReportCycleModel,
} from 'src/models/orgExpenseReports.models'
import { TransactionDateType } from 'src/models/transaction.models'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import {
  issuerGroupedRCNOrgExpenseReportCycles,
  useQueryOrgExpenseReportCycleReports,
  useQueryOrgExpenseReportCycles,
} from 'src/queries/orgExpenseReports.queries'
import {
  defaultQueryCacheOptions,
  savedViewsQueryKeys,
} from 'src/queries/queries.constants'
import {
  SavedViewsModel,
  useCreateSavedView,
} from 'src/queries/savedViews.queries'
import { useQueryUsersSearch } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { parseBackendErrors } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface TransactionsListFilterProps {
  defaultFilters: TransactionsFilters // only used for resetting single filters without immediately applying the change
  initialFilterValues: TransactionsFilters
  filterValues: TransactionsFilters
  hasViewChange: boolean
  selectedFiltersCount: number
  handleFilterChange: (newFilters: Partial<TransactionsFilters>) => void
  resetFilters: () => void
  handleResetToDefaultView?: () => void
  resetSingleFilter: (key: keyof TransactionsFilters) => void
  apiQueryParams: TransactionsFiltersBody // for txn download
  viewingReport?: boolean // to handle non-navigation changes for org expense report quick filter buttons
  reportId?: string
  context: TransactionReportType
  visibleFilters: TransactionFilterOptions[]
  isTransactionsFetching?: boolean
  handleDownloadClick?: () => void
  handleFullScreenClick?: () => void
  isFullScreen?: boolean
  savedViews: SavedViewsModel[]
  getCurrentColumnData?: () => string | undefined
}

const TransactionsListFilter: React.FC<TransactionsListFilterProps> = (
  props,
): React.ReactElement => {
  const queryClient = useQueryClient()
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const extendLocation = useExtendLocation()
  const location = useLocation()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const {
    mutateAsync: createSavedView,
    isPending: isSavingView,
    error,
  } = useCreateSavedView()

  // FETCHING DATA FOR FILTER DROPDOWNS -------------------------------------

  // expense category labels
  const { data: expenseCategories } = useQueryExpenseCategories({
    filters: { active: true },
  })
  const requiredExpenseCategories =
    expenseCategories &&
    expenseCategories.filter((category) => category.requiredForTransactions)
      .length > 0

  // users
  const recipientOptions = useSelector(selectAllUsersFromState)
  const [userSearch, setUserSearch] = useState('')

  const {
    data: usersData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    complete: usersComplete,
  } = useQueryUsersSearch({
    filters: {
      q: userSearch !== '' ? userSearch : undefined,
      count: 25,
    },
    queryCacheOptions: defaultQueryCacheOptions,
    additionalKey: extendLocation.matchedPath,
  })

  const userInfiniteScrollProps = useDropdownInfiniteScroll({
    currentPage: usersData?.page,
    isLoading,
    isFetchingNextPage,
    isComplete: usersComplete,
    fetchNextPage,
    currentSearch: userSearch,
    setSearch: setUserSearch,
  })

  // teams
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: departmentsList } = useQueryDepartments({
    organizationId: organization?.id,
    includeArchived: false,
  })
  const departmentsOptions = useMemo(
    () =>
      departmentsList?.map((department) => ({
        value: department.id,
        details: {
          text: department.name,
        },
      })) ?? [],
    [departmentsList],
  )

  // ------------------------------------------------------------------------

  // STATE TRACKERS FOR UI FILTERS ------------------------------------------

  const [searchFilter, setSearchFilter] = useState(
    props.filterValues.search || '',
  )
  const debouncedSearchFilter = useDebounceValue(searchFilter, 250)
  const userSearchEditInProgressRef = useRef(false)
  const [dateType, setDateType] = useState(props.initialFilterValues.dateType)
  const [startDateFilter, setStartDateFilter] = useState(
    props.initialFilterValues.since,
  )
  const [endDateFilter, setEndDateFilter] = useState(
    props.initialFilterValues.until,
  )
  const [minClearingBillingCentsFilter, setMinClearingBillingCentsFilter] =
    useState<number | undefined>(
      props.initialFilterValues.minClearingBillingCents !== undefined
        ? Number(props.initialFilterValues.minClearingBillingCents)
        : undefined,
    )
  const [maxClearingBillingCentsFilter, setMaxClearingBillingCentsFilter] =
    useState<number | undefined>(
      props.initialFilterValues.maxClearingBillingCents !== undefined
        ? Number(props.initialFilterValues.maxClearingBillingCents)
        : undefined,
    )
  const {
    set: statusSet,
    setSet: setStatusSet,
    handleSelection: handleStatusSelection,
  } = useStringSetState(props.initialFilterValues.status)
  const {
    set: originSet,
    setSet: setOriginSet,
    handleSelection: handleOriginSelection,
  } = useStringSetState(props.initialFilterValues.source)
  const {
    set: expenseCategoryStatusesSet,
    setSet: setExpenseCategoryStatusesSet,
    handleSelection: handleExpenseCategoryStatusesSelection,
  } = useStringSetState(props.initialFilterValues.expenseCategoryStatuses)
  const {
    fields: expenseCategoriesSet,
    setFields: setExpenseCategoriesFields,
    onFieldChange: handleExpenseCategoriesSelection,
  } = useFormFields<Record<string, Set<string>>>(
    props.initialFilterValues.expenseCategories || {},
    (): FormFieldsValidation<Record<string, Set<string>>> =>
      ({}) as FormFieldsValidation<Record<string, Set<string>>>,
  )
  const {
    set: receiptStatusSet,
    setSet: setReceiptStatusSet,
    handleSelection: handleReceiptStatusSelection,
  } = useStringSetState(props.initialFilterValues.receiptStatus)
  const [receiptRequired, setReceiptRequired] = useState<boolean | undefined>(
    props.initialFilterValues.receiptRequired,
  )
  const {
    set: receiptRecoveryStatusSet,
    setSet: setReceiptRecoveryStatusSet,
    handleSelection: handleReceiptRecoveryStatusSelection,
  } = useStringSetState(props.initialFilterValues.receiptRecoveryStatus)
  const {
    set: syncStatusesSet,
    setSet: setSyncStatusesSet,
    handleSelection: handleSyncStatusesSelection,
  } = useStringSetState(props.initialFilterValues.syncStatuses)
  const {
    set: receiptAttachmentSyncStatusesSet,
    setSet: setReceiptAttachmentSyncStatusesSet,
    handleSelection: handleReceiptAttachmentSyncStatusesSelection,
  } = useStringSetState(props.initialFilterValues.receiptAttachmentSyncStatuses)
  const {
    set: recipientSet,
    setSet: setRecipientSet,
    handleSelection: handleRecipientSelection,
  } = useStringSetState(props.initialFilterValues.recipient)
  const {
    set: departmentsSet,
    setSet: setDepartmentsSet,
    handleSelection: handleDepartmentsSelection,
  } = useStringSetState(props.initialFilterValues.departments)
  const {
    set: creditCardSet,
    setSet: setCreditCardSet,
    handleSelection: handleCreditCardSelection,
  } = useStringSetState(props.initialFilterValues.creditCardId)
  const {
    set: mccGroupSet,
    setSet: setMccGroupSet,
    handleSelection: handleMccGroupSelection,
  } = useStringSetState(props.initialFilterValues.mccGroup)
  const {
    set: reviewStatusSet,
    setSet: setReviewStatusSet,
    handleSelection: handleReviewStatusesSelection,
  } = useStringSetState(props.initialFilterValues.reviewStatus)

  // dates & cycles
  const { data: cycles } = useQueryOrgExpenseReportCycles({
    select: issuerGroupedRCNOrgExpenseReportCycles,
    enabled: props.context === TransactionReportType.AccountingTransactions,
  })

  const selectedCreditCardCycle = cycles
    ?.flatMap((group: OrgExpenseReportCycleGroup) => group.rcns)
    .filter(
      (cycle: OrgExpenseReportCycleModel) =>
        cycle.accountId === creditCardSet.values().next().value,
    )[0]

  const { data: cycleReports } = useQueryOrgExpenseReportCycleReports({
    cycleId: selectedCreditCardCycle?.id,
    enabled: !!selectedCreditCardCycle?.cycleType,
  })

  const sortedStatements =
    cycleReports?.sort(
      (a, b) => new Date(b.until).getTime() - new Date(a.until).getTime(),
    ) || []
  const currentStatement =
    sortedStatements.length > 0 ? sortedStatements[0] : undefined
  const previousStatement =
    sortedStatements.length > 1 ? sortedStatements[1] : undefined

  const deriveDateRangeType = (
    since?: Moment,
    until?: Moment,
  ): DateRangeType | undefined => {
    if (!since || !until) return undefined

    const curSince = currentStatement?.since
      ? moment(currentStatement.since).startOf('day')
      : undefined
    const curUntil = currentStatement?.until
      ? moment(currentStatement.until).endOf('day')
      : undefined
    const prevSince = previousStatement?.since
      ? moment(previousStatement.since).startOf('day')
      : undefined
    const prevUntil = previousStatement?.until
      ? moment(previousStatement.until).endOf('day')
      : undefined

    const sameRange = (
      aS: moment.Moment,
      aE: moment.Moment,
      bS?: moment.Moment,
      bE?: moment.Moment,
    ) => !!bS && !!bE && aS.isSame(bS, 'day') && aE.isSame(bE, 'day')

    if (sameRange(since, until, curSince, curUntil))
      return DateRangeType.CurrentStatement
    if (sameRange(since, until, prevSince, prevUntil))
      return DateRangeType.PreviousStatement

    const todayStart = moment().startOf('day')
    const todayEnd = moment().endOf('day')

    const thisMonthStart = moment().startOf('month')
    const thisMonthEnd = moment().endOf('month')

    const lastMonthStart = moment().subtract(1, 'month').startOf('month')
    const lastMonthEnd = moment().subtract(1, 'month').endOf('month')

    const last30Start = moment().subtract(30, 'days').startOf('day')
    const last30End = todayEnd

    const last90Start = moment().subtract(90, 'days').startOf('day')
    const last90End = todayEnd

    if (since.isSame(todayStart, 'day') && until.isSame(todayEnd, 'day')) {
      return DateRangeType.Today
    }
    if (
      since.isSame(thisMonthStart, 'day') &&
      until.isSame(thisMonthEnd, 'day')
    ) {
      return DateRangeType.ThisMonth
    }
    if (
      since.isSame(lastMonthStart, 'day') &&
      until.isSame(lastMonthEnd, 'day')
    ) {
      return DateRangeType.LastMonth
    }
    if (
      props.context === TransactionReportType.NeedsApproval &&
      since.isSame(last90Start, 'day') &&
      (!until || until.isSame(last90End, 'day'))
    ) {
      // only use this label in this context
      // because the date range is non-editable
      return DateRangeType.Last90Days
    }
    if (
      since.isSame(last30Start, 'day') &&
      (!until || until.isSame(last30End, 'day'))
    ) {
      return DateRangeType.Last30Days
    }
    return DateRangeType.Custom
  }

  const selectedViewId = useMemo(() => {
    const exists = props.savedViews.some(
      (view) => view.id === props.filterValues.viewId,
    )
    return exists ? props.filterValues.viewId : undefined
  }, [props.filterValues.viewId, props.savedViews])

  const [dateRangeType, setDateRangeType] = useState<DateRangeType | undefined>(
    () =>
      deriveDateRangeType(props.filterValues.since, props.filterValues.until),
  )
  const lastAppliedSavedViewDateRangeKeyRef = useRef<{
    viewId?: string
    key?: string
  }>({})

  useEffect(() => {
    const next = deriveDateRangeType(
      props.filterValues.since,
      props.filterValues.until,
    )
    setDateRangeType(next)
  }, [props.filterValues.since, props.filterValues.until])

  useEffect(() => {
    const nextSearchFilter = props.filterValues.search || ''
    userSearchEditInProgressRef.current = false
    setSearchFilter(nextSearchFilter)
  }, [props.filterValues.search])

  useEffect(() => {
    if (!userSearchEditInProgressRef.current) return
    const currentSearchFilter = props.filterValues.search || ''
    if (debouncedSearchFilter === currentSearchFilter) {
      userSearchEditInProgressRef.current = false
      return
    }

    userSearchEditInProgressRef.current = false
    props.handleFilterChange({ search: debouncedSearchFilter })
  }, [
    debouncedSearchFilter,
    props.filterValues.search,
    props.handleFilterChange,
  ])

  useEffect(() => {
    setStartDateFilter(props.filterValues.since)
    setEndDateFilter(props.filterValues.until)
  }, [props.filterValues.since, props.filterValues.until])

  useEffect(() => {
    if (!selectedViewId) {
      lastAppliedSavedViewDateRangeKeyRef.current = {}
      return
    }

    const selectedView = props.savedViews.find(
      (view) => view.id === selectedViewId,
    )
    const dateRangeTypeKey = selectedView?.filters?.dateRangeType
    if (!dateRangeTypeKey || dateRangeTypeKey === 'Custom') return

    if (
      lastAppliedSavedViewDateRangeKeyRef.current.viewId === selectedViewId &&
      lastAppliedSavedViewDateRangeKeyRef.current.key === dateRangeTypeKey
    ) {
      return
    }

    const dateRangeTypeValue = getDateRangeTypeFromKey(dateRangeTypeKey)
    if (!dateRangeTypeValue) return

    lastAppliedSavedViewDateRangeKeyRef.current = {
      viewId: selectedViewId,
      key: dateRangeTypeKey,
    }
    setDateRangeType(dateRangeTypeValue)
  }, [selectedViewId, props.savedViews])

  const customDateText =
    startDateFilter !== undefined
      ? endDateFilter !== undefined
        ? `${startDateFilter.format('M/D/YY')} - ${endDateFilter?.format('M/D/YY') || ''}`
        : `After ${startDateFilter.format('M/D/YY')}`
      : endDateFilter !== undefined
        ? `On or before ${endDateFilter.format('M/D/YY')}`
        : `Choose a date range`

  let dateRangeText = ''
  switch (dateRangeType) {
    case DateRangeType.Last30Days:
      dateRangeText = translate('Last 30 Days')
      break
    case DateRangeType.Today:
    case DateRangeType.ThisMonth:
    case DateRangeType.LastMonth:
      dateRangeText = translate(dateRangeType)
      break
    case DateRangeType.Custom:
      dateRangeText = customDateText
      break
    case DateRangeType.CurrentStatement:
      if (currentStatement && creditCardSet.size === 1) {
        dateRangeText = translate('Current Statement')
      } else {
        dateRangeText = customDateText
      }
      break
    case DateRangeType.PreviousStatement:
      if (previousStatement && creditCardSet.size === 1) {
        dateRangeText = translate('Previous Statement')
      } else {
        dateRangeText = customDateText
      }
      break
    case DateRangeType.Last90Days:
      dateRangeText = translate('Last 90 Days')
      break
    default:
      dateRangeText = translate('Last 30 Days')
      break
  }

  const handleSetType = (type: DateRangeType | undefined): void => {
    setDateRangeType(type)
    if (!type) {
      props.handleFilterChange({
        since: props.defaultFilters.since,
        until: props.defaultFilters.until,
      })
    }
  }

  const handleSetRange = (since: Moment, until: Moment): void => {
    setStartDateFilter(since)
    setEndDateFilter(until)
    props.handleFilterChange({ since: since?.clone(), until: until?.clone() })
  }

  const handleSetCreditCardId = (
    value: DropdownValue<CreditCardDropdownOptionDetails>,
  ): void => {
    const next = new Set(creditCardSet)
    if (value.selected) {
      next.add(value.value)
    } else {
      next.delete(value.value)
    }
    setCreditCardSet(next)
    props.handleFilterChange({ creditCardId: next })
  }

  const handleSetSince = (val: Moment | undefined): void => {
    setStartDateFilter(val)
    props.handleFilterChange({ since: val?.clone() })
  }

  const handleSetUntil = (val: Moment | undefined): void => {
    setEndDateFilter(val)
    props.handleFilterChange({ until: val?.clone() })
  }

  const resetCreditCardSet = (): void => {
    setCreditCardSet(new Set())
    props.resetSingleFilter('creditCardId')
  }

  useEffect(() => {
    if (creditCardSet.size !== 1) return
    if (!dateRangeType) return
    if (
      dateRangeType &&
      ![
        DateRangeType.CurrentStatement,
        DateRangeType.PreviousStatement,
      ].includes(dateRangeType)
    )
      return

    const target =
      dateRangeType === DateRangeType.CurrentStatement
        ? currentStatement
        : previousStatement

    if (!target?.since || !target?.until) return

    const since = moment(target.since).startOf('day')
    const until = moment(target.until).endOf('day')

    const alreadyApplied =
      startDateFilter?.isSame(since, 'day') &&
      endDateFilter?.isSame(until, 'day')

    if (!alreadyApplied) {
      handleSetRange(since, until)
    }
  }, [
    dateRangeType,
    currentStatement,
    previousStatement,
    creditCardSet,
    startDateFilter,
    endDateFilter,
    handleSetRange,
  ])

  // ------------------------------------------------------------------------

  // NEW VIEW STATE ---------------------------------------------------------

  const selectedViewDisplayName = useMemo(() => {
    if (selectedViewId === undefined) {
      if (props.hasViewChange) {
        return translate('Unsaved View')
      }
      return translate('Default View')
    }
    const savedViewName = props.savedViews.find(
      (view) => view.id === selectedViewId,
    )?.displayName
    return savedViewName || translate('Unsaved View')
  }, [selectedViewId, props.savedViews, props.hasViewChange, translate])

  const [isSaveNewViewOpen, setIsSaveNewViewOpen] = useState(false)
  const {
    fields,
    fieldsErrors,
    onFieldChange,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<{ displayName: string }>({ displayName: '' }, () => ({
    displayName: [stringIsEmpty()],
  }))

  const [showDeleteViewConfirmationModal, setShowDeleteViewConfirmationModal] =
    useState<boolean>(false)
  const [deletingViewId, setDeletingViewId] = useState<string | undefined>(
    undefined,
  )

  const handleCancelSaveNewView = useCallback((): void => {
    setIsSaveNewViewOpen(false)
    onFieldChange('displayName', '')
  }, [onFieldChange])

  const handleSetSavedView = useCallback(
    (viewId: string): void => {
      userSearchEditInProgressRef.current = false
      analytics?.track('saved txn view', { action: 'applied' })
      props.handleFilterChange({ viewId })
    },
    [analytics, props.handleFilterChange],
  )

  const handleConfirmSaveNewView = useCallback(async (): Promise<void> => {
    if (!setErrorsAndReturnDidPassValidation()) return

    try {
      const dateRangeTypeKey = getDateRangeTypeKey(dateRangeType)

      const savedViewFilters: TransactionsFiltersBody = {
        ...props.apiQueryParams,
      }

      if (dateRangeTypeKey && dateRangeTypeKey !== 'Custom') {
        savedViewFilters.dateRangeType = dateRangeTypeKey
        delete savedViewFilters.since
        delete savedViewFilters.until
        delete savedViewFilters.sinceClearedAt
        delete savedViewFilters.untilClearedAt
      } else {
        delete savedViewFilters.dateRangeType
      }

      const savedView = await createSavedView({
        context: props.context,
        body: {
          displayName: fields['displayName'].trim(),
          filters: savedViewFilters,
          columnData: props.getCurrentColumnData?.() || null,
        },
      })
      await queryClient.invalidateQueries({
        queryKey: savedViewsQueryKeys.all(),
      })
      props.handleFilterChange({
        ...props.filterValues,
        viewId: savedView.id,
      })
      analytics?.track('saved txn view', { action: 'created' })
      handleCancelSaveNewView()
    } catch (_err) {
      return
    }
  }, [
    setErrorsAndReturnDidPassValidation,
    createSavedView,
    props.context,
    fields,
    dateRangeType,
    props.apiQueryParams,
    queryClient,
    props.handleFilterChange,
    props.filterValues,
    props.getCurrentColumnData,
    analytics,
    handleCancelSaveNewView,
  ])

  // ------------------------------------------------------------------------

  const resetUIFiltersToCurrent = useCallback((): void => {
    setSearchFilter(props.filterValues.search || '')
    setDateType(props.filterValues.dateType)
    setStartDateFilter(props.filterValues.since)
    setEndDateFilter(props.filterValues.until)
    setMinClearingBillingCentsFilter(props.filterValues.minClearingBillingCents)
    setMaxClearingBillingCentsFilter(props.filterValues.maxClearingBillingCents)
    setStatusSet(props.filterValues.status || new Set<string>())
    setOriginSet(props.filterValues.source || new Set<string>())
    setExpenseCategoryStatusesSet(
      props.filterValues.expenseCategoryStatuses || new Set<string>(),
    )
    setExpenseCategoriesFields(props.filterValues.expenseCategories || {})
    setReceiptStatusSet(props.filterValues.receiptStatus || new Set<string>())
    setReceiptRecoveryStatusSet(
      props.filterValues.receiptRecoveryStatus || new Set<string>(),
    )
    setReceiptRequired(props.filterValues.receiptRequired)
    setSyncStatusesSet(props.filterValues.syncStatuses || new Set<string>())
    setReceiptAttachmentSyncStatusesSet(
      props.filterValues.receiptAttachmentSyncStatuses || new Set<string>(),
    )
    setRecipientSet(props.filterValues.recipient || new Set<string>())
    setDepartmentsSet(props.filterValues.departments || new Set<string>())
    setCreditCardSet(props.filterValues.creditCardId || new Set<string>())
    setMccGroupSet(props.filterValues.mccGroup || new Set<string>())
    setReviewStatusSet(props.filterValues.reviewStatus || new Set<string>())
  }, [props.filterValues])

  const filtersList: React.ReactElement[] = useMemo(() => {
    const filters: React.ReactElement[] = []

    // Date Filter (Date Type + Date Range)
    if (props.visibleFilters.includes(TransactionFilterOptions.Date)) {
      filters.push(
        <TransactionDateTypeFilter
          key="date-filter"
          dateType={dateType || TransactionDateType.Transaction}
          setDateType={setDateType}
          startDateFilter={startDateFilter}
          setStartDateFilter={setStartDateFilter}
          endDateFilter={endDateFilter}
          setEndDateFilter={setEndDateFilter}
          filterValues={props.filterValues}
          isTransactionsFetching={props.isTransactionsFetching}
          showDateRange={props.visibleFilters.includes(
            TransactionFilterOptions.TransactionDateRange,
          )}
        />,
      )
    }

    // Transaction Amount Range Filter
    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.TransactionAmountRange,
      )
    ) {
      filters.push(
        <TransactionAmountRangeFilter
          key="amount-range-filter"
          minClearingBillingCentsFilter={minClearingBillingCentsFilter}
          setMinClearingBillingCentsFilter={setMinClearingBillingCentsFilter}
          maxClearingBillingCentsFilter={maxClearingBillingCentsFilter}
          setMaxClearingBillingCentsFilter={setMaxClearingBillingCentsFilter}
        />,
      )
    }

    // Transaction Status Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.TransactionStatus)
    ) {
      filters.push(
        <TransactionStatusFilter
          key="status-filter"
          statusSet={statusSet}
          handleStatusSelection={handleStatusSelection}
        />,
      )
    }

    // Transaction Origin Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.TransactionOrigin)
    ) {
      filters.push(
        <TransactionOriginFilter
          key="origin-filter"
          originSet={originSet}
          handleOriginSelection={handleOriginSelection}
        />,
      )
    }

    // Expense Category Status Filter
    if (
      (activeIntegration !== undefined || requiredExpenseCategories) &&
      props.visibleFilters.includes(
        TransactionFilterOptions.ExpenseCategoryStatus,
      )
    ) {
      filters.push(
        <ExpenseCategoryStatusFilter
          key="expense-category-status-filter"
          expenseCategoryStatusesSet={expenseCategoryStatusesSet}
          handleExpenseCategoryStatusesSelection={
            handleExpenseCategoryStatusesSelection
          }
        />,
      )
    }

    // Expense Category Labels Filter
    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.ExpenseCategoryLabels,
      ) &&
      expenseCategories &&
      expenseCategories.length > 0
    ) {
      filters.push(
        <ExpenseCategoryLabelsFilter
          key="expense-category-labels-filter"
          expenseCategories={expenseCategories}
          expenseCategoriesSet={expenseCategoriesSet}
          handleExpenseCategoriesSelection={handleExpenseCategoriesSelection}
        />,
      )
    }

    // Receipt Status Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.ReceiptStatus)) {
      filters.push(
        <TransactionReceiptStatusFilter
          key="receipt-status-filter"
          receiptStatusSet={receiptStatusSet}
          handleReceiptStatusSelection={handleReceiptStatusSelection}
          receiptRequired={receiptRequired}
          setReceiptRequired={setReceiptRequired}
        />,
      )
    }

    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.ReceiptRecoveryStatus,
      )
    ) {
      filters.push(
        <TransactionReceiptRecoveryStatusFilter
          key="receipt-recovery-status-filter"
          receiptRecoveryStatusSet={receiptRecoveryStatusSet}
          handleReceiptRecoveryStatusSelection={
            handleReceiptRecoveryStatusSelection
          }
        />,
      )
    }

    // Review Status Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.ReviewStatus)) {
      filters.push(
        <TransactionReviewStatusFilter
          key="review-status-filter"
          reviewStatusSet={reviewStatusSet}
          handleReviewStatusesSelection={handleReviewStatusesSelection}
        />,
      )
    }

    // TODO: modify UI of these filters

    // Transaction Sync Status Filter
    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.TransactionSyncStatus,
      )
    ) {
      filters.push(
        <TransactionSyncStatusFilter
          key="transaction-sync-status-filter"
          syncStatusesSet={syncStatusesSet}
          handleSyncStatusesSelection={handleSyncStatusesSelection}
          activeIntegration={activeIntegration}
        />,
      )
    }

    if (
      props.visibleFilters.includes(TransactionFilterOptions.ReceiptSyncStatus)
    ) {
      filters.push(
        <ReceiptSyncStatusFilter
          key="receipt-sync-status-filter"
          receiptAttachmentSyncStatusesSet={receiptAttachmentSyncStatusesSet}
          handleReceiptAttachmentSyncStatusesSelection={
            handleReceiptAttachmentSyncStatusesSelection
          }
        />,
      )
    }

    // Card User Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.CardUser)) {
      filters.push(
        <CardUserFilter
          key="card-user-filter"
          recipientSet={recipientSet}
          handleRecipientSelection={handleRecipientSelection}
          setRecipientSet={setRecipientSet}
          recipientOptions={recipientOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    // Departments Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.Departments) &&
      departmentsOptions.length > 0
    ) {
      filters.push(
        <DepartmentsFilter
          key="departments-filter"
          departmentsSet={departmentsSet}
          handleDepartmentsSelection={handleDepartmentsSelection}
          setDepartmentsSet={setDepartmentsSet}
          departmentsOptions={departmentsOptions}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    // Account Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.Account)) {
      filters.push(
        <AccountFilter
          key="account-filter"
          creditCardSet={creditCardSet}
          handleCreditCardSelection={handleCreditCardSelection}
          setCreditCardSet={setCreditCardSet}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    // Merchant Category Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.MerchantCategory)
    ) {
      filters.push(
        <TransactionMerchantCategoryFilter
          key="merchant-category-filter"
          mccGroupSet={mccGroupSet}
          handleMccGroupSelection={handleMccGroupSelection}
          setMccGroupSet={setMccGroupSet}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    return filters
  }, [
    props.visibleFilters,
    dateType,
    startDateFilter,
    endDateFilter,
    props.filterValues,
    props.isTransactionsFetching,
    minClearingBillingCentsFilter,
    maxClearingBillingCentsFilter,
    statusSet,
    handleStatusSelection,
    originSet,
    handleOriginSelection,
    expenseCategoryStatusesSet,
    handleExpenseCategoryStatusesSelection,
    requiredExpenseCategories,
    expenseCategories,
    expenseCategoriesSet,
    handleExpenseCategoriesSelection,
    receiptStatusSet,
    handleReceiptStatusSelection,
    receiptRequired,
    setReceiptRequired,
    receiptRecoveryStatusSet,
    handleReceiptRecoveryStatusSelection,
    reviewStatusSet,
    handleReviewStatusesSelection,
    syncStatusesSet,
    handleSyncStatusesSelection,
    receiptAttachmentSyncStatusesSet,
    handleReceiptAttachmentSyncStatusesSelection,
    recipientSet,
    handleRecipientSelection,
    setRecipientSet,
    recipientOptions,
    userInfiniteScrollProps,
    props.defaultFilters,
    departmentsSet,
    handleDepartmentsSelection,
    setDepartmentsSet,
    departmentsOptions,
    creditCardSet,
    handleCreditCardSelection,
    setCreditCardSet,
    mccGroupSet,
    handleMccGroupSelection,
    setMccGroupSet,
    locale,
  ])

  return (
    <>
      <UtilityToolbar
        header={
          <UtilityToolbarDropdown
            buttonText={
              <span
                css={css`
                  display: flow;
                  max-width: 140px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  @media (max-width: 1199px) {
                    display: none;
                  }
                `}
              >
                {selectedViewDisplayName}
              </span>
            }
            dropdown={
              <SavedViewsDropdown
                handleSetView={handleSetSavedView}
                handleResetToDefault={
                  props.handleResetToDefaultView || props.resetFilters
                }
                handleSaveNewView={() => setIsSaveNewViewOpen(true)}
                handleDeleteView={(viewId) => {
                  setDeletingViewId(viewId)
                  setShowDeleteViewConfirmationModal(true)
                }}
                views={props.savedViews}
                selectedViewId={selectedViewId}
                hasViewChange={props.hasViewChange}
              />
            }
            image={<SavedViewsIcon />}
            testId={'savedViewsDropdown'}
            selected={selectedViewId === undefined && props.hasViewChange}
            showCircleIndicator={
              selectedViewId === undefined && props.selectedFiltersCount !== 0
            }
            alignment={'left'}
          />
        }
        searchProps={{
          value: searchFilter,
          onChange: (val: string): void => {
            userSearchEditInProgressRef.current = true
            setSearchFilter(val)
          },
          placeholder: translateSingleString(
            locale,
            'Enter an amount, merchant name, or card name',
          ),
        }}
        userDropdownProps={
          ![
            TransactionReportType.MyTransactions,
            TransactionReportType.ExpenseReport,
          ].includes(props.context)
            ? {
                isCreatable: false,
                placeholder: translateSingleString(locale, 'All users'),
                chooseOption: (val: string): void => {
                  const newSet = handleRecipientSelection(val)
                  props.handleFilterChange({ recipient: newSet })
                },
                isMulti: true,
                selectedValues: recipientSet,
                optionGroups: [
                  {
                    label: translate('Suggested'),
                    options: recipientOptions || [],
                  },
                ],
                onResetClick: (): void => {
                  setRecipientSet(
                    props.defaultFilters.recipient || new Set<string>(),
                  )
                  props.resetSingleFilter('recipient')
                },
                infiniteScrollProps: userInfiniteScrollProps,
                startAndKeepOpen: true,
                // use embedded style: true to update the dropdown style to match the new designs (which are still pending)
              }
            : undefined
        }
        filtersFlyoutProps={{
          title: translate('Transaction Filters'),
          onReset: props.resetFilters,
          loading: props.isTransactionsFetching,
          selectedFiltersCount: props.selectedFiltersCount,
          filters: (
            <>
              {filtersList.map((f, i) => (
                <div key={`filter_${i}`}>
                  {f}
                  {i !== filtersList?.length - 1 && (
                    <DividerLineDiv
                      css={css`
                        margin: 20px 0px;
                      `}
                    />
                  )}
                </div>
              ))}
            </>
          ),
          onSave: () => {
            props.handleFilterChange({
              search: searchFilter,
              dateType: dateType,
              since: startDateFilter,
              until: endDateFilter,
              minClearingBillingCents: minClearingBillingCentsFilter,
              maxClearingBillingCents: maxClearingBillingCentsFilter,
              status: statusSet,
              source: originSet,
              expenseCategoryStatuses: expenseCategoryStatusesSet,
              expenseCategories: expenseCategoriesSet,
              receiptStatus: receiptStatusSet,
              receiptRecoveryStatus: receiptRecoveryStatusSet,
              receiptRequired: receiptRequired,
              syncStatuses: syncStatusesSet,
              receiptAttachmentSyncStatuses: receiptAttachmentSyncStatusesSet,
              recipient: recipientSet,
              departments: departmentsSet,
              creditCardId: creditCardSet,
              mccGroup: mccGroupSet,
              reviewStatus: reviewStatusSet,
            })
          },
          // reset UI filters on flyout open to avoid a race condition if we were to do it on flyout close
          onOpenFlyout: resetUIFiltersToCurrent,
        }}
        dateFilterDropdownProps={
          props.context !== TransactionReportType.ExpenseReport
            ? {
                buttonText: (
                  <span
                    css={css`
                      @media (max-width: 1199px) {
                        display: none;
                      }
                    `}
                  >{`${dateRangeText}`}</span>
                ),
                dropdown: (
                  <DateFilterDropdown
                    showStatementOptions={creditCardSet.size === 1}
                    dateRangeType={dateRangeType}
                    handleSetType={handleSetType}
                    handleSetRange={handleSetRange}
                    handleSetSince={handleSetSince}
                    handleSetUntil={handleSetUntil}
                    customSince={startDateFilter}
                    customUntil={endDateFilter}
                    cycleId={selectedCreditCardCycle?.id}
                    currentStatement={currentStatement}
                    previousStatement={previousStatement}
                  />
                ),
                image: <SvgCalendarNew />,
                testId: 'dateFilterDropdown',
                selected:
                  (dateRangeType !== undefined &&
                    dateRangeType !== DateRangeType.Last30Days) ||
                  (props.filterValues.viewId !== undefined &&
                    dateRangeType !== undefined),
                disabled: props.context === TransactionReportType.NeedsApproval,
              }
            : undefined
        }
        creditCardDropdownProps={
          props.context === TransactionReportType.AccountingTransactions
            ? {
                selectedCreditCardIds: creditCardSet,
                onSelect: (
                  val: DropdownValue<CreditCardDropdownOptionDetails>,
                ): void => {
                  handleSetCreditCardId(val)
                },
                onReset: (): void => {
                  resetCreditCardSet()
                },
                placeholder: 'All Card Accounts',
                location: location.pathname,
              }
            : undefined
        }
        utilityToolbarDownloadProps={{
          onClick: props.handleDownloadClick,
        }}
        utilityToolbarAdditionalAction={
          props.handleFullScreenClick ? (
            props.isFullScreen ? (
              <Button
                className="selected"
                variant="toolbar"
                dropShadow={false}
                onClick={props.handleFullScreenClick}
                aria-label={translate('Exit Full Screen')}
                testId="transactionsFullScreenBtn"
              >
                <SvgToolbarFullscreenExit fill="currentColor" />
              </Button>
            ) : (
              <HoverableLink
                ariaLabel={translate('Enter Full Screen')}
                onClick={props.handleFullScreenClick}
                testId="transactionsFullScreenBtn"
              >
                <SvgToolbarFullscreen />
              </HoverableLink>
            )
          ) : undefined
        }
      >
        {isSaveNewViewOpen && (
          <div
            className={'flex-row gap-18 justify-between align-end'}
            css={css`
              padding: ${spacing(4)} ${spacing(5)};
              border-radius: ${cornerRadius};
              border: 1px solid ${base_colorContentBorder};
              background: ${base_colorSurfaceSecondary};
            `}
          >
            <TextInput
              label={translate('View Name')}
              placeholder={translate('Name this view...')}
              testId={'viewNameInput'}
              requirement={'required'}
              className={'flex-1'}
              value={fields['displayName']}
              onChange={(e) => onFieldChange('displayName', e.target.value)}
              errorText={
                fieldsErrors['displayName'] ||
                (error?.response?.data
                  ? parseBackendErrors(error.response.data).topLevelError
                  : undefined)
              }
            />
            <div className={'flex-row gap-6'}>
              <Button variant={'secondary'} onClick={handleCancelSaveNewView}>
                {translate('Cancel')}
              </Button>
              <Button
                disabled={isSavingView}
                onClick={() => void handleConfirmSaveNewView()}
              >
                {translate('Confirm Save')}
              </Button>
            </div>
          </div>
        )}
      </UtilityToolbar>
      <DeleteViewConfirmationModal
        viewId={deletingViewId}
        viewDisplayName={
          props.savedViews.find((view) => view.id === deletingViewId)
            ?.displayName || ''
        }
        context={props.context}
        isOpen={showDeleteViewConfirmationModal}
        onClose={() => setShowDeleteViewConfirmationModal(false)}
      />
    </>
  )
}

export default React.memo(TransactionsListFilter)
